// Reading stats: heartbeat-based session tracker. The reader calls
// startSession() when the user opens a book and tick() every N seconds
// while they're actively reading. We accumulate seconds-read per session
// and roll them into per-book aggregates (total, sessions, streak).
//
// Per-book preferences (voice / rate / provider) live here too so the
// reader can restore the book's preferred voice on reopen.

import { db } from "./db";
import type { BookStats, PerBookPrefs, ReadingSession } from "@/types/stats";

function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  // a < b assumed; returns whole days.
  const ad = new Date(a + "T00:00:00Z").getTime();
  const bd = new Date(b + "T00:00:00Z").getTime();
  return Math.round((bd - ad) / 86_400_000);
}

export async function startSession(bookId: string): Promise<ReadingSession> {
  const s: ReadingSession = {
    id: uid(),
    bookId,
    startedAt: Date.now(),
    seconds: 0,
    day: today(),
  };
  await db.sessions.add(s);
  return s;
}

/** Bump the session's seconds-read counter and roll into book aggregates. */
export async function tickSession(
  sessionId: string,
  bookId: string,
  deltaSeconds: number
): Promise<void> {
  if (deltaSeconds <= 0) return;
  const s = await db.sessions.get(sessionId);
  if (!s) return;
  s.seconds += deltaSeconds;
  await db.sessions.put(s);

  // Update aggregate.
  const cur = (await db.bookStats.get(bookId)) ?? {
    id: bookId,
    totalSeconds: 0,
    sessions: 0,
    currentStreak: 0,
    longestStreak: 0,
  };
  cur.totalSeconds += deltaSeconds;
  // Bump session count only the first time we add seconds in this session
  // (i.e. when this is a fresh session record). We detect that by checking
  // if pre-tick seconds were 0.
  if (s.seconds === deltaSeconds) cur.sessions += 1;

  const day = today();
  if (cur.lastDay !== day) {
    if (cur.lastDay) {
      const gap = daysBetween(cur.lastDay, day);
      cur.currentStreak = gap === 1 ? cur.currentStreak + 1 : 1;
    } else {
      cur.currentStreak = 1;
    }
    cur.longestStreak = Math.max(cur.longestStreak, cur.currentStreak);
    cur.lastDay = day;
  }
  await db.bookStats.put(cur);
}

export async function getBookStats(bookId: string): Promise<BookStats | null> {
  return (await db.bookStats.get(bookId)) ?? null;
}

export async function listSessions(bookId: string): Promise<ReadingSession[]> {
  return db.sessions
    .where("bookId")
    .equals(bookId)
    .reverse()
    .sortBy("startedAt");
}

// ─── Per-book preferences ────────────────────────────────────────────

export async function getBookPrefs(bookId: string): Promise<PerBookPrefs | null> {
  return (await db.bookPrefs.get(bookId)) ?? null;
}

export async function setBookPrefs(
  bookId: string,
  patch: Partial<PerBookPrefs>
): Promise<void> {
  const cur = (await db.bookPrefs.get(bookId)) ?? { id: bookId };
  await db.bookPrefs.put({ ...cur, ...patch, id: bookId });
}

// ─── Reading-time estimation ─────────────────────────────────────────

/** Narration speed, in characters of source text per second of audio, at
 *  playback rate 1.0. NOT a guess — measured against the live endpoints on
 *  2026-09-29 over ten real paragraphs from the library:
 *
 *    Piper  (en_US-amy-medium): mean 15.55 chars/s, sd 0.95, range 14.1–17.0
 *    Kokoro (af_heart):         mean 16.72 chars/s
 *
 *  16.1 is the midpoint. Characters rather than words because the ratio is
 *  far more stable across languages and punctuation, and because we count
 *  characters exactly. */
const NARRATION_CHARS_PER_SEC = 16.1;

/** Silent reading speed. This one IS a convention (a common adult average
 *  for prose), not a measurement — the app does not yet observe how fast
 *  the user actually reads. Learning a per-user rate from time-on-section
 *  is a worthwhile follow-up but needs care: page-flip timing is noisy,
 *  and a reader who leaves the app open would skew it badly. */
const SILENT_WPM = 250;

/**
 * Time left in the book, for reading and for listening.
 *
 * Returns null when the book's real size isn't known. That is deliberate:
 * until 2026-09-29 this function fell back to a hardcoded 80,000 words,
 * and because the caller never actually supplied a count, EVERY book
 * rendered 80000/250 = 320 minutes — the identical "5 hrs 20 mins left"
 * that appeared on 13 of 19 books, on a 919 KB Don Quijote and a 41 KB art
 * catalogue alike. A missing estimate is honest; a fabricated one makes
 * everything else on screen less believable.
 *
 * Read and listen are genuinely different numbers — across the live
 * library the listening figure runs roughly 40% longer — so the caller
 * picks by what the user is actually doing.
 */
export function estimateRemaining(opts: {
  progress: number;
  wordCount?: number;
  charCount?: number;
  ttsRate: number;
  silentWpm?: number;
}): { readSeconds: number; listenSeconds: number } | null {
  if (!opts.wordCount || opts.wordCount <= 0) return null;
  const left = 1 - Math.max(0, Math.min(1, opts.progress));
  const wordsLeft = opts.wordCount * left;
  const silentWpm = opts.silentWpm ?? SILENT_WPM;

  // Prefer the character count for narration (that's what the measurement
  // is in). Fall back to words × a typical 5.5 chars/word if an older
  // record only carries a word count.
  const charsTotal = opts.charCount && opts.charCount > 0
    ? opts.charCount
    : opts.wordCount * 5.5;
  const charsLeft = charsTotal * left;
  const rate = Math.max(0.5, Math.min(2, opts.ttsRate));

  return {
    readSeconds: Math.round((wordsLeft / silentWpm) * 60),
    listenSeconds: Math.round(charsLeft / (NARRATION_CHARS_PER_SEC * rate)),
  };
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}
